/** @jsxRuntime classic */

/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import React, { useEffect, useRef, useState, memo, useMemo, useCallback } from 'react';
import Rodal from 'rodal';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'emotion-theming';
import { useAsync } from 'react-async';
import { home, migratoryWebCitizen, phytosanitaryWeb, sanitarianRegistersWeb, tdCarWeb } from '../../Colors';
import useMediaQuery from '../../../hooks/use-media-query';
import ScrollWrapper from '../../ScrollWrapper';
import { Button, FormError, ButtonSelector, DropDownCombo, TextArea, SpinnerSmall } from '../../../core';
import { DivRow, DivColumn, Title, InputText, FormLabel } from '../InputsUI';
import styles from './modalStyle';
import modalCustomStyle from './utils';
import { useCountries, getStateByName, getCityByName } from '../../../queries';
import { getEntityNameFromId } from '../../../core/advanced/procedure/queries';
import useLocation from '../../../hooks/use-locations';
import masksJson from '../../../utils/phoneMask.json';
const themes = {
  home: {
    alpha: home.cerulean,
    beta: home.frenchPass,
    gamma: home.cerulean,
    delta: home.fireEngineRed,
    epsilon: home.white
  },
  migratoryWebCitizen: {
    alpha: migratoryWebCitizen.cerulean,
    beta: migratoryWebCitizen.frenchPass,
    gamma: migratoryWebCitizen.cerulean,
    delta: migratoryWebCitizen.fireEngineRed,
    epsilon: migratoryWebCitizen.white
  },
  sanitarianRegistersWeb: {
    alpha: sanitarianRegistersWeb.deepCerulean,
    beta: sanitarianRegistersWeb.frenchPass,
    gamma: sanitarianRegistersWeb.deepCerulean,
    delta: sanitarianRegistersWeb.fireEngineRed,
    epsilon: sanitarianRegistersWeb.white
  },
  phytosanitaryWeb: {
    alpha: phytosanitaryWeb.mantis,
    beta: phytosanitaryWeb.frenchPass,
    gamma: phytosanitaryWeb.mantis,
    delta: phytosanitaryWeb.mantis,
    epsilon: phytosanitaryWeb.sherpaBlue
  },
  tdCarWeb: {
    alpha: tdCarWeb.cerulean,
    beta: tdCarWeb.frenchPass,
    gamma: tdCarWeb.cerulean,
    delta: tdCarWeb.fireEngineRed,
    epsilon: tdCarWeb.white
  }
};
/**
 * This component represents a modal to add new importer
 * @param {boolean} isVisible Determines if the modal is visible or not.
 * @param {function} setIsVisible Function triggered when close the modal to hide it.
 * @param {function} onClickSaveImporter Function is triggered when the use button is pressed, and save the new importer.
 * @param {object} formik Formik.
 */

const NewCompany = /*#__PURE__*/memo(({
  isVisible,
  setIsVisible,
  onClickSaveImporter,
  formik,
  isPendingData,
  title = 'Agregar nuevo importador a la lista de empresas'
}) => {
  var _formik$values2;

  const {
    theme
  } = useTheme();
  const {
    isMobile
  } = useMediaQuery();
  const {
    t
  } = useTranslation();
  const {
    data: countries = []
  } = useCountries();
  const {
    states,
    state,
    cities,
    districts,
    onLocationChange
  } = useLocation();
  const [disableCountry, setDisableCountry] = useState(false);
  const [manageFindCompany, setManageFindCompany] = useState(false);
  const [textError, setTextError] = useState(false);
  const stateRef = useRef(false);
  const cityRef = useRef(false);
  const isFromCR = formik.values.businessTypeId === 1;
  const crId = useMemo(() => countries.find(x => x.ISO31661A3 === 'CRI'), [countries]);
  const [phoneMask, setPhoneMask] = useState({});
  const {
    isPending,
    run
  } = useAsync({
    async deferFn(legalId) {
      try {
        const {
          name
        } = await getEntityNameFromId(legalId[0]);
        formik.setFieldValue('name', name);
        setManageFindCompany(true);
      } catch (error) {
        setTextError(error.message);
        formik.setFieldValue('name', '', false);
      }
    }

  });

  const onChangeState = ({
    id,
    name
  }) => {
    formik.setValues({ ...formik.values,
      state: name,
      stateId: id,
      cityId: '',
      districtId: ''
    });
    onLocationChange({
      target: {
        value: id,
        name: 'state'
      }
    });
  };

  const onChangeCity = ({
    id,
    name
  }) => {
    formik.setValues({ ...formik.values,
      cityId: id,
      city: name,
      districtId: ''
    });
    onLocationChange({
      target: {
        value: id,
        name: 'city'
      }
    });
  };

  const onChangeDistrict = ({
    id,
    name
  }) => {
    formik.setValues({ ...formik.values,
      districtId: id,
      district: name
    });
    onLocationChange({
      target: {
        value: id,
        name: 'district'
      }
    });
  };

  const onChangeCountry = ({
    id
  }) => {
    formik.setFieldValue('countryId', id);
  };

  const onChangeCompanyType = useCallback(value => {
    formik.setValues({ ...formik.values,
      countryId: value === 1 ? (crId === null || crId === void 0 ? void 0 : crId.id) || '' : '',
      legalId: '',
      businessTypeId: value
    });
    setDisableCountry(value === 1);

    if (value !== 1) {
      setManageFindCompany(false);
    }
  }, [crId, formik.values]);

  const onChangeLegalId = legalId => {
    setTextError('');
    formik.setValues({ ...formik.values,
      legalId
    });

    if (isFromCR && legalId.length === 10) {
      run(legalId);
    }
  };

  useEffect(() => {
    var _formik$values$state;

    async function fetch() {
      const [stateData] = await getStateByName(formik.values.state);

      if (stateData === null || stateData === void 0 ? void 0 : stateData.id) {
        onLocationChange({
          target: {
            value: stateData === null || stateData === void 0 ? void 0 : stateData.id,
            name: 'state'
          }
        });
      }

      stateRef.current = true;
    }

    if (((_formik$values$state = formik.values.state) === null || _formik$values$state === void 0 ? void 0 : _formik$values$state.length) && !stateRef.current) {
      fetch();
    }
  }, [formik.values.state, onLocationChange]);
  useEffect(() => {
    var _state$toString;

    async function fetch() {
      const [cityData] = await getCityByName({
        name: formik.values.city,
        stateId: state
      });

      if (cityData === null || cityData === void 0 ? void 0 : cityData.id) {
        onLocationChange({
          target: {
            value: cityData === null || cityData === void 0 ? void 0 : cityData.id,
            name: 'city'
          }
        });
      }

      cityRef.current = true;
    }

    if ((state === null || state === void 0 ? void 0 : (_state$toString = state.toString()) === null || _state$toString === void 0 ? void 0 : _state$toString.length) && !cityRef.current) {
      fetch();
    }
  }, [formik.values.city, onLocationChange, state]);
  useEffect(() => {
    var _formik$values;

    if ((_formik$values = formik.values) === null || _formik$values === void 0 ? void 0 : _formik$values.countryId) {
      const currentCountry = countries.find(x => x.id === formik.values.countryId);
      setPhoneMask(masksJson[currentCountry.ISO31661A2] ? {
        mask: masksJson[currentCountry.ISO31661A2]
      } : {
        regex: '^[0-9]*$'
      });
    }

    formik.setFieldValue('telephone', '');
    formik.setFieldValue('fax', '');
  }, [formik.values.countryId]);
  return jsx(Rodal, {
    visible: isVisible,
    showCloseButton: false,
    width: 1112,
    height: 650,
    onClose: () => {},
    customStyles: { ...modalCustomStyle({
        isMobile,
        width: 1112,
        height: 'auto',
        maxHeight: 650
      }),
      borderRadius: '20px',
      overflow: 'hidden'
    }
  }, jsx("div", {
    css: [styles, {
      padding: '35px 46px 0 46px'
    }]
  }, jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 30
    }
  }, jsx(DivColumn, {
    className: "modals-column"
  }, jsx("h1", {
    className: "modals-title",
    style: {
      fontSize: 20,
      marginTop: 'unset',
      color: themes[theme].alpha
    }
  }, t(title)))), jsx(ScrollWrapper, null, jsx("form", {
    onSubmit: formik.handleSubmit,
    style: {
      width: '100%',
      maxWidth: 609
    }
  }, jsx(Title, {
    css: {
      fontSize: 14,
      margin: '0 0 25px 0',
      color: themes[theme].alpha,
      borderBottom: `1px solid ${themes[theme].alpha}`,
      paddingBottom: 1,
      display: 'inline-block'
    }
  }, t('Datos empresa')), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Tipo de empresa'), ":*"), jsx(ButtonSelector, {
    value: formik.values.businessTypeId,
    onChange: value => onChangeCompanyType(value),
    data: [{
      value: 1,
      text: t('Nacional')
    }, {
      value: 2,
      text: t('Extranjera')
    }],
    error: formik.errors.businessTypeId,
    touched: formik.touched.businessTypeId
  }), jsx(FormError, {
    msg: formik.errors.businessTypeId,
    touched: formik.touched.businessTypeId
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('País de la empresa'), ":*"), jsx(DropDownCombo, {
    placeholder: t('Ingresar_placeholder'),
    items: countries,
    style: {
      margin: 0,
      width: '100%'
    },
    value: ((_formik$values2 = formik.values) === null || _formik$values2 === void 0 ? void 0 : _formik$values2.countryId) || '',
    onChange: onChangeCountry,
    error: formik.errors.countryId,
    touched: formik.touched.countryId,
    disabled: disableCountry
  }), jsx(FormError, {
    msg: formik.errors.countryId,
    touched: formik.touched.countryId
  }))), isFromCR && jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Cédula jurídica'), ":*"), jsx(InputText, {
    id: "legalId",
    name: "legalId",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: onChangeLegalId,
    value: formik.values.legalId,
    width: "100%",
    mask: {
      mask: '9-999-999999'
    },
    error: formik.errors.legalId,
    touched: formik.touched.legalId,
    disabled: isPending
  }, isPending && jsx(SpinnerSmall, {
    style: {
      width: 20,
      height: 20,
      position: 'absolute',
      top: 16,
      right: 15,
      color: themes[theme].alpha
    }
  })), jsx("span", {
    style: {
      color: '#CE1625',
      fontSize: 12
    }
  }, textError), jsx(FormError, {
    msg: formik.errors.legalId,
    touched: formik.touched.legalId
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Nombre de la empresa'), ":*"), jsx(InputText, {
    id: "name",
    name: "name",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.name,
    error: formik.errors.name,
    touched: formik.touched.name,
    width: "100%",
    mask: {
      regex: '^[a-zA-Z0-9áéíóúüñÁÉÍÓÚÜÑ.#&/," ]*$'
    },
    maxLength: 250,
    disabled: manageFindCompany
  }), jsx(FormError, {
    msg: formik.errors.name,
    touched: formik.touched.name
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Teléfono'), ":"), jsx(InputText, {
    id: "telephone",
    name: "telephone",
    autoComplete: "off",
    mask: phoneMask,
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.telephone,
    error: formik.errors.telephone,
    touched: formik.touched.telephone,
    width: "100%",
    maxLength: 100
  }), jsx(FormError, {
    msg: formik.errors.telephone,
    touched: formik.touched.telephone
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Correo electrónico'), ":*"), jsx(InputText, {
    id: "email",
    name: "email",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    value: formik.values.email,
    error: formik.errors.email,
    touched: formik.touched.email,
    width: "100%",
    onChange: e => {
      let value = e;

      if (value !== '') {
        value = value.toLowerCase().replace(/[ ]/g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      }

      formik.setFieldValue('email', value);
      return value;
    },
    maxLength: "128"
  }), jsx(FormError, {
    msg: formik.errors.email,
    touched: formik.touched.email
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Fax'), ":"), jsx(InputText, {
    id: "fax",
    name: "fax",
    mask: phoneMask,
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.fax,
    error: formik.errors.fax,
    touched: formik.touched.fax,
    width: "100%",
    maxLength: 30
  }), jsx(FormError, {
    msg: formik.errors.fax,
    touched: formik.touched.fax
  }))), jsx(Title, {
    css: {
      fontSize: 14,
      margin: '0 0 25px 0',
      color: themes[theme].alpha,
      borderBottom: `1px solid ${themes[theme].alpha}`,
      paddingBottom: 1,
      display: 'inline-block'
    }
  }, t('Dirección')), isFromCR ? jsx(React.Fragment, null, jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Provincia'), ":*"), jsx(DropDownCombo, {
    id: "stateId",
    name: "stateId",
    items: states,
    placeholder: t('Seleccionar_placeholder'),
    onChange: onChangeState,
    style: {
      width: '100%'
    },
    value: formik.values.stateId,
    error: formik.errors.stateId,
    touched: formik.touched.stateId
  }), jsx(FormError, {
    msg: formik.errors.stateId,
    touched: formik.touched.stateId
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Cantón'), ":*"), jsx(DropDownCombo, {
    id: "cityId",
    name: "cityId",
    items: cities,
    placeholder: t('Seleccionar_placeholder'),
    onChange: onChangeCity,
    style: {
      width: '100%'
    },
    value: formik.values.cityId,
    error: formik.errors.cityId,
    touched: formik.touched.cityId
  }), jsx(FormError, {
    msg: formik.errors.cityId,
    touched: formik.touched.cityId
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Distrito'), ":*"), jsx(DropDownCombo, {
    id: "districId",
    name: "districId",
    items: districts,
    placeholder: t('Seleccionar_placeholder'),
    onChange: onChangeDistrict,
    style: {
      width: '100%'
    },
    value: formik.values.districtId,
    error: formik.errors.districtId,
    touched: formik.touched.districtId
  }), jsx(FormError, {
    msg: formik.errors.districtId,
    touched: formik.touched.districtId
  })))) : jsx(React.Fragment, null, jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Estado'), ":*"), jsx(InputText, {
    id: "stateName",
    name: "stateName",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.stateName,
    error: formik.errors.stateName,
    touched: formik.touched.stateName,
    width: "100%",
    mask: {
      regex: '^[a-zA-Z0-9áéíóúüñÁÉÍÓÚÜÑ.#&/," ]*$'
    },
    maxLength: 100
  }), jsx(FormError, {
    msg: formik.errors.stateName,
    touched: formik.touched.stateName
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Ciudad'), ":*"), jsx(InputText, {
    id: "cityName",
    name: "cityName",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.cityName,
    error: formik.errors.cityName,
    touched: formik.touched.cityName,
    width: "100%",
    mask: {
      regex: '^[a-zA-Z0-9áéíóúüñÁÉÍÓÚÜÑ.#&/," ]*$'
    },
    maxLength: 100
  }), jsx(FormError, {
    msg: formik.errors.cityName,
    touched: formik.touched.cityName
  }))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 25
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Distrito'), ":*"), jsx(InputText, {
    id: "districtName",
    name: "districtName",
    autoComplete: "off",
    placeholder: t('Ingresar_placeholder'),
    onChange: formik.handleChange,
    value: formik.values.districtName,
    error: formik.errors.districtName,
    touched: formik.touched.districtName,
    width: "100%",
    mask: {
      regex: '^[a-zA-Z0-9áéíóúüñÁÉÍÓÚÜÑ.#&/," ]*$'
    },
    maxLength: 100
  }), jsx(FormError, {
    msg: formik.errors.districtName,
    touched: formik.touched.districtName
  })))), jsx(DivRow, {
    className: "modals-row",
    style: {
      marginBottom: 50
    }
  }, jsx(DivColumn, null, jsx(FormLabel, {
    containerStyle: {
      margin: 0
    }
  }, t('Otras señas'), ":"), jsx(TextArea, {
    id: "address",
    name: "address",
    autoComplete: "off",
    placeholder: "Ingresar",
    width: "100%",
    maxLength: "150",
    style: {
      height: '170px',
      fontSize: '14px !important'
    },
    value: formik.values.address,
    showCount: true,
    showCountStyle: {
      color: `${themes[theme].beta} !important`,
      bottom: '11px !important',
      right: '19px !important',
      fontSize: '15px !important'
    },
    onChange: formik.handleChange
  }), jsx(FormError, {
    msg: formik.errors.address,
    touched: formik.touched.address
  }))))), jsx("div", {
    css: css`
              position: relative;
              ::before {
                content: '';
                position: absolute;
                top: 0;
                right: -100px;
                left: -100px;
                border-top: 1px solid ${themes[theme].beta};
              }
            `
  }), jsx("div", {
    className: "button-container",
    style: {
      paddingTop: 15,
      paddingBottom: 3
    }
  }, jsx(Button, {
    size: "md-icon",
    outline: true,
    className: "button blue",
    style: {
      color: themes[theme].gamma,
      border: `2px solid ${themes[theme].gamma}`,
      width: 139
    } // disabled={isCanceling}
    ,
    onClick: setIsVisible
  }, t('Cancelar')), jsx(Button, {
    size: "md-icon",
    className: "button red",
    style: {
      background: themes[theme].delta,
      color: themes[theme].epsilon,
      width: 139
    },
    onClick: onClickSaveImporter
  }, t('Guardar'), isPendingData && jsx(SpinnerSmall, {
    style: {
      width: 21,
      height: 21,
      marginLeft: 5
    }
  })))));
}, (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps));
export default NewCompany;